// Liens à jeton : schémas de l'API `/api/links` et format de `GET /t/:token.json`, communs au
// serveur, au front (écran « Mes liens ») et aux widgets qui consomment le JSON.
import { z } from 'zod';
import type { HintResult, WindowStatus } from './templates/index.ts';

/** Expiration : instant ISO 8601 (décalage permis) ou `null` pour « jamais ». */
const expiresAtSchema = z.iso.datetime({ offset: true }).nullable();

export const linkCreateSchema = z.object({
  tableId: z.number().int().positive(),
  name: z.string().trim().min(1).max(100),
  expiresAt: expiresAtSchema.optional(),
});

/** Modification d'un lien actif. La révocation est définitive. */
export const linkUpdateSchema = z
  .object({
    name: z.string().trim().min(1).max(100).optional(),
    expiresAt: expiresAtSchema.optional(),
    revoked: z.literal(true).optional(),
  })
  .refine((input) => Object.keys(input).length > 0, { error: 'Aucune modification demandée' });

export interface LinkDto {
  id: number;
  name: string;
  tableId: number;
  tableName: string;
  token: string;
  createdAt: string;
  lastUsedAt: string | null;
  hits: number;
  expiresAt: string | null;
  revoked: boolean;
}

/** Fenêtres de navigation (kayak, bateau) : aide à la décision, renvoi au bulletin côtier officiel. */
export const NAVIGATION_ADVISORY = {
  label: 'Aide à la décision · vérifiez avant de partir',
  bulletinLabel: 'Bulletin côtier Météo-France',
  bulletinUrl: 'https://meteofrance.com/meteo-marine',
} as const;

export interface SharedWindowDto {
  /**
   * Profil composé (pêche) : slug « espece[+esche][+poste] », nom « Bar · leurre · bord ».
   * Pêche seulement : `species`, `poste` et `esche`, slugs des parties (`null` pour une partie
   * absente) ; ajoutés après coup, absents des autres catégories.
   */
  profile: { slug: string; name: string; category: string; species?: string; poste?: string | null; esche?: string | null };
  status: WindowStatus;
  /** Jours AAAA-MM-JJ (Europe/Paris) ; `null` sans fenêtre trouvée. */
  start: string | null;
  end: string | null;
  /** Créneau : heures locales de début et de fin (exclue), le jour `start` et le jour `end`. */
  startHour: number | null;
  endHour: number | null;
  score: number | null;
  /** Présent pour une fenêtre de navigation. */
  advisory: typeof NAVIGATION_ADVISORY | null;
  /**
   * Indices actifs, affichage seul (« Fortes pluies récentes (85 mm sur 10 j) : poussée
   * possiblement abondante ») : `text` prêt à afficher, `value` mesurée. Ajouté après coup,
   * absent quand la fenêtre n'en a pas.
   */
  hints?: HintResult[];
}

/** Réponse de `GET /t/:token.json` : fenêtres groupées par lieu, nom seul, jamais de coordonnées. */
export interface SharedTableDto {
  table: { name: string };
  /** Dernier calcul des fenêtres (ISO), `null` s'il n'y en a pas encore. */
  updatedAt: string | null;
  locations: { name: string; windows: SharedWindowDto[] }[];
}
